import { IStream } from "../../store/types";
import { IMessage, ISubscriber } from "../messages/types";
import { IQuestions } from "../useGetQuestions";
import { IQuestionLike } from "../useLikeQuestion";
import type { ILiveFile } from "../../types/liveFile";

export type EventType =
	| "question"
	| "chat"
	| "reaction"
	| "error"
	| "approveQuestion"
	| "deleteQuestion"
	| "editQuestion"
	| "manageQuestion"
	| "likeQuestion"
	| "live_started"
	| "live_ended"
	| "broadcastQuestion"
	| "userAlreadyConnected"
	| "userVerified"
	| "pong"
	| "live_stream"
	| "participantJoined"
	| "participantLeft"
	| "requestAuditorRoster"
	| "inviteAuditor"
	| "inviteAuditorResponse"
	| "poll_published"
	| "poll_unpublished"
	| "poll_deleted"
	| "poll_updated"
	| "poll_vote"
	| "document_published"
	| "document_unpublished"
	| "document_deleted"
	| "live_files_refresh";

export interface IReactionEvent {
	id: number;
	liveId: number;
	createdAt: string;
	liveChatId: number;
	liveInteractionId: number;
	subscriberId: number;
	interaction: {
		id: number;
		interaction: string;
	};
	subscriber: ISubscriber;
}
export interface IPong {
	id: string;
	time: number;
	dolocation: string;
	users: string;
}

export interface IUserVerified {
	type: "userVerified";
	data: {
		message: string;
		userId: string;
	};
}

export interface IPollOption {
	id: number;
	createdAt: string;
	updatedAt: string | null;
	poll_id: number;
	poll_option: string;
	vote_count: number;
}

export interface IPoll {
	id: number;
	createdAt: string;
	updatedAt: string | null;
	liveId: number;
	question: string;
	isPublished: boolean;
	creatorId: number;
	creator: {
		id: number;
		email: string;
		first_name: string;
		last_name: string;
		username: string;
	};
	options: IPollOption[];
}

export interface IPollVoteEvent {
	pollId: number;
	optionId: number;
	vote_count: number;
}

export type DocumentSocketPayload =
	| { file: ILiveFile; liveId?: number }
	| { fileId: number; liveId: number }
	| { liveId: number };

export interface IAuditorInvitation {
	/** Full name of the auditor being invited (must match the listenerFullName) */
	fullName: string;
	liveId: number;
	/** URL the invited auditor should open to join the LiveKit session */
	joinUrl: string;
	/** Optional name of the admin sending the invite (for display) */
	invitedBy?: string;
	/** Unique id used by clients to deduplicate notifications */
	invitationId: string;
	sentAt: string;
}

export interface IAuditorInvitationResponse {
	/** Auditor full name responding (lower-cased compared on the receiver) */
	fullName: string;
	liveId: number;
	/** Original invitationId so the admin can correlate */
	invitationId: string;
	response: "accepted" | "declined";
	respondedAt: string;
}

export interface IParticipantLeft {
	fullName: string;
	liveId: number;
	leftAt: string;
}

export interface IEvent {
	type: EventType;
	data:
		| IQuestions
		| IMessage
		| IReactionEvent
		| IUserVerified
		| IPong
		| IStream
		| IPoll
		| IPollVoteEvent
		| IAuditorInvitation
		| IAuditorInvitationResponse
		| IParticipantLeft
		| { pollId: number }
		| number
		| DocumentSocketPayload
		| {
				data: {
					questionLike: IQuestionLike | null;
					result: string;
				};
				questionId: number;
		  };
}

export const SocketEvents = {
	question: "question",
	chat: "chat",
	reaction: "reaction",
	error: "error",
	approveQuestion: "approveQuestion",
	liveStarted: "live_started",
	liveEnded: "live_ended",
	pong: "pong",
	deleteQuestion: "deleteQuestion",
	editQuestion: "editQuestion",
	likeQuestion: "likeQuestion",
	manageQuestion: "manageQuestion",
	broadcastQuestion: "broadcastQuestion",
	userAlreadyConnected: "userAlreadyConnected",
	userVerified: "userVerified",
	liveStream: "live_stream",
	participantJoined: "participantJoined",
	participantLeft: "participantLeft",
	requestAuditorRoster: "requestAuditorRoster",
	inviteAuditor: "inviteAuditor",
	inviteAuditorResponse: "inviteAuditorResponse",
	pollPublished: "poll_published",
	pollUnpublished: "poll_unpublished",
	pollDeleted: "poll_deleted",
	pollUpdated: "poll_updated",
	pollVote: "poll_vote",
	documentPublished: "document_published",
	documentUnpublished: "document_unpublished",
	documentDeleted: "document_deleted",
	liveFilesRefresh: "live_files_refresh",
} as const;
